import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const services = [
  {
    id: "Interior & Exterior Painting",
    tab: "Painting",
    testid: "service-tab-painting",
    num: "01",
    title: "Interior & Exterior Painting",
    img: "/work/cabinets-after.jpg",
    alt: "Finished painted wooden cabinets by Xtra Clean",
    features: [
      "Full interior repaints & accent walls",
      "Weather-tough exterior coatings",
      "Meticulous prep, crisp cut lines",
      "Cabinet painting & refinishing",
    ],
    turnaround: "Every job fully masked & protected",
  },
  {
    id: "Wood Refinishing & Stain Restoration",
    tab: "Wood",
    testid: "service-tab-wood",
    num: "02",
    title: "Wood Refinishing & Stain Restoration",
    img: "/work/floor-staining.jpg",
    alt: "Applying a glossy finish to a hardwood floor by a picture window",
    features: [
      "Hardwood floor sanding & refinishing",
      "Deck & fence staining",
      "Furniture & cabinetry restoration",
      "Custom stain color matching",
    ],
    turnaround: "Dust-controlled sanding equipment",
  },
  {
    id: "Floor Deep Cleaning",
    tab: "Cleaning",
    testid: "service-tab-cleaning",
    num: "03",
    title: "Floor Deep Cleaning",
    img: "/work/carpet-cleaning.jpg",
    alt: "Deep extraction carpet cleaning with a professional wand",
    features: [
      "Carpet deep extraction cleaning",
      "Upholstery & sofa cleaning",
      "Tile & grout restoration",
      "Pet stain & odor treatment",
    ],
    turnaround: "Homes & businesses welcome",
  },
  {
    id: "Floor Sales & Installation",
    tab: "Installation",
    testid: "service-tab-installation",
    num: "04",
    title: "Floor Sales & Installation",
    img: "/work/carpet-stairs.jpg",
    alt: "Newly installed plush carpet on a wooden staircase",
    features: [
      "We sell quality flooring materials",
      "Tile, vinyl & laminate installation",
      "Hardwood floor installation",
      "Old floor removal & haul-away",
    ],
    turnaround: "Free in-home measurement",
  },
];

const ease = [0.16, 1, 0.3, 1];

export default function Services({ onQuote }) {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#FAFAFA] py-20 lg:py-32"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease }}
          className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-600 font-semibold mb-4"
        >
          What We Do
        </motion.p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14 lg:mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease }}
            className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-xl"
          >
            Four crafts. One flawless standard.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-mono-x text-xs uppercase tracking-[0.2em] text-zinc-500"
          >
            Free quotes on every service
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
          {services.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease }}
              className="group bg-white border border-zinc-200 hover:border-red-600/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col"
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 bg-red-600 text-white font-mono-x text-xs font-semibold tracking-widest px-3 py-1.5">
                  {s.num}
                </span>
              </div>
              <div className="p-6 lg:p-8 flex flex-col flex-1">
                <h3 className="font-display font-bold tracking-tight text-xl sm:text-2xl mb-4">
                  {s.title}
                </h3>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-600">
                      <Check size={16} className="text-red-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <p className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-5">
                  {s.turnaround}
                </p>
                <button
                  data-testid={`service-quote-btn-${s.num}`}
                  onClick={() => onQuote(s.id)}
                  className="flex items-center justify-between border-2 border-zinc-900 text-zinc-900 hover:bg-red-600 hover:border-red-600 hover:text-white transition-all duration-200 px-5 py-3 font-mono-x text-xs font-semibold uppercase tracking-wider active:scale-[0.98]"
                >
                  Get quote for this
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
